import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import { ErrorOption } from 'react-hook-form';
import { RawCreateParams, z } from 'zod';
import { ApiError, UserTypeEnum } from '../api';

class DrfApiError {
  [key: string]: string[] | DrfApiError;
}

export interface ShowFieldErrors {
  [key: string]: boolean | { [key: string]: ShowFieldErrors };
}

export const UserTypeLabel: Record<UserTypeEnum, string> = {
  [UserTypeEnum.HEALTHCARE_ENTITY_REP]: 'Healthcare Entity Rep',
  [UserTypeEnum.PROVIDER]: 'Provider',
};

export const UserTypeShortLabel: Record<UserTypeEnum, string> = {
  [UserTypeEnum.HEALTHCARE_ENTITY_REP]: 'Rep',
  [UserTypeEnum.PROVIDER]: 'Provider',
};

const isArrayIndex = (string: string) => /^\d+$/.test(string);

function flattenErrorObject(errorObj: DrfApiError, parentKey = ''): ShowFieldErrors {
  return Object.entries(errorObj).reduce((flattened, [key, value]) => {
    const newKey = parentKey + (parentKey ? '__' : '') + key;

    if (
      isObject(value) &&
      !Array.isArray(value) &&
      Object.keys(value).length > 0 &&
      isArrayIndex(Object.keys(value)[0])
    ) {
      const nestedFlattened = flattenErrorObject(value, newKey);
      Object.assign(flattened, nestedFlattened);
    } else {
      flattened[newKey] = value;
    }

    return flattened as ShowFieldErrors;
  }, {});
}

function generateShowFieldErrorFromFlattened(flattened: ShowFieldErrors) {
  if (!isBoolean(flattened) && !isObject(flattened)) {
    return true;
  }
  return Object.entries(flattened).reduce((prev, [key, value]) => {
    if (isBoolean(value)) {
      return {
        ...prev,
        [key]: true,
      };
    }
    if (isObject(value)) {
      return {
        ...prev,
        [key]: Object.entries(value).reduce(
          (prev, [index, indexError]) => ({
            ...prev,
            [index]: generateShowFieldErrorFromFlattened(indexError),
          }),
          {}
        ),
      };
    }
  }, {});
}

export function generateShowFieldErrors(errors: DrfApiError) {
  if (!errors || (isObject(errors) && isEmpty(errors))) return {};
  const flattenedErrors = flattenErrorObject(errors);
  return generateShowFieldErrorFromFlattened(flattenedErrors);
}

export function generateZEnumFromEnum(enumType: object) {
  if (!enumType) {
    return z.null();
  }
  const [firstKey, ...rest] = Object.keys(enumType);

  // Create a custom error map for the enum
  const customErrorMap: z.ZodErrorMap = (issue, _ctx) => {
    if (issue.code === 'invalid_enum_value') {
      return { message: 'Invalid value' };
    }
    return { message: 'Invalid value' };
  };

  // Set the error map before creating the enum
  z.setErrorMap(customErrorMap);

  return z.enum([firstKey, ...rest]);
}

export const nullToEmptyString = (value: string | null) => (value === null ? '' : value);

function isFileUploadError(error: any): error is { file: string[] } {
  return 'file' in error && error.file !== undefined;
}

function getFormErrorMessageFromApiError(error: any): string {
  if (typeof error === 'string') {
    return error;
  }

  if (isFileUploadError(error)) {
    return getFormErrorMessageFromApiError(error.file);
  }

  if (Array.isArray(error) && typeof error[0] === 'string') {
    return error.join(';\n');
  }

  return 'Unknown error';
}

export function setFormErrorsFromApiErrors(
  apiErrors: ApiError,
  setError: (name: string, error: ErrorOption) => void
): void {
  // No response body or HTML instead of JSON - show generic error
  if (!apiErrors.body || typeof apiErrors.body === 'string') {
    return setError('root.non_field_errors', {
      message: 'The request failed. Please try again.',
    });
  }

  const { non_field_errors, detail, ...fieldErrors } = apiErrors.body;

  // Global generic error
  if (detail) {
    setError('root.non_field_errors', { message: detail });
  }

  // Non field errors
  if (non_field_errors) {
    setError('root.non_field_errors', { message: non_field_errors.join(';\n') });
  }

  // Field errors
  if (fieldErrors) {
    Object.entries(fieldErrors).forEach(([fieldName, errors]: [string, string[]]) => {
      if (typeof errors[0] === 'string') {
        setError(fieldName, { message: errors.join(';\n') });
      } else {
        Object.entries(errors).forEach(([key, error]) => {
          // Skip empty objects
          if (typeof error !== 'object' || Object.keys(error).length > 0) {
            setError(`${fieldName}.${key}`, { message: getFormErrorMessageFromApiError(error) });
          }
        });
      }
    });
  }
}

// Define the type for options
type Option = Record<string, any>;

// Function to deduplicate and optionally sort options
export const cleanAndSortOptions = (
  options: Option[],
  keyName = 'value',
  valueName = 'label',
  sort = true
): Option[] => {
  if (!options || options.length === 0) {
    return [];
  }

  const uniqueOptions = new Map<any, Option>();
  options.forEach((option) => {
    if (!uniqueOptions.has(option[keyName])) {
      uniqueOptions.set(option[keyName], option);
    }
  });

  const uniqueArray = Array.from(uniqueOptions.values());

  if (sort) {
    uniqueArray.sort((a, b) => a[valueName].localeCompare(b[valueName])); // Adjust sorting logic as needed
  }

  return uniqueArray;
};
